import { Field as AField, useFieldContext } from '@ark-ui/solid/field';
import { createEffect, useContext, For, splitProps, createContext, createSignal, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { cn } from 'tailwind-variants';
import { FormContext } from './form.tsx';
import type { Accessor, ComponentProps, JSX, ParentProps } from 'solid-js';

type Store<T extends Record<string, unknown> | Array<unknown>> = ReturnType<typeof createStore<T>>;

export type InputCTX = Partial<JSX.AriaAttributes & { name: string }>;
export const InputContext = createContext<Store<InputCTX>>([{}, () => {}]);

export type DescriptionCTX = { id: string };
export const DescriptionContext = createContext<{
	descriptions: Array<DescriptionCTX>;
	add: (props: DescriptionCTX) => void;
	remove: (props: DescriptionCTX) => void;
}>({ descriptions: [], add: () => {}, remove: () => {} });

export type FieldProps = ParentProps<{
	'aria-describedby'?: string;
	class?: string;
	name: string;
	id?: string;
	required?: boolean;
	invalid?: boolean;
}>;

export function Field(props: FieldProps) {
	const [, wrapperProps] = splitProps(props, ['name', 'required']);
	const { fieldErrors } = useContext(FormContext);
	const [input, setInput] = createStore<InputCTX>({
		// oxlint-disable-next-line solid/reactivity
		name: props.name,
	});

	const [errors, setErrors] = createSignal<Array<string>>([]);

	createEffect(() => {
		setErrors(fieldErrors()[props.name] ?? []);
		if (fieldErrors()[props.name] && fieldErrors()[props.name]!.length) {
			setInput('aria-invalid', true);
		} else {
			setInput('aria-invalid', undefined);
		}
	});

	return (
		<InputContext.Provider value={[input, setInput]}>
			<ErrorContext.Provider value={errors}>
				<AField.Root
					invalid={!!input['aria-invalid'] || props.invalid}
					{...wrapperProps}
					class={cn('group/field flex w-full flex-col justify-start gap-1', wrapperProps.class)}
				>
					{wrapperProps.children}
				</AField.Root>
			</ErrorContext.Provider>
		</InputContext.Provider>
	);
}

Field.Label = (props: ComponentProps<typeof AField.Label>) => {
	return (
		<AField.Label {...props} class={cn('flex gap-1 text-lg font-medium', props.class)}>
			{props.children}
			<Field.Required />
		</AField.Label>
	);
};

Field.Required = () => {
	const ctx = useFieldContext();
	return (
		<Show when={ctx().invalid && ctx().required}>
			<span aria-hidden class="font-bold text-rose-700">
				*
			</span>
		</Show>
	);
};

Field.Description = (props: ComponentProps<typeof AField.HelperText>) => {
	return (
		<AField.HelperText
			asChild={(props) => (
				<div
					{...props()}
					class="flex w-full grow flex-row items-center gap-1 text-sm text-zinc-600 dark:text-zinc-400"
				/>
			)}
		>
			{props.children}
		</AField.HelperText>
	);
};

Field.Errors = (props: ComponentProps<typeof AField.ErrorText>) => {
	const errors = useContext(ErrorContext);
	return (
		<AField.ErrorText class="flex flex-col text-rose-500">
			<For each={errors()}>{(issue) => <p>{issue}</p>}</For>
			{props.children}
		</AField.ErrorText>
	);
};
Field.ErrorCustom = (props: ParentProps<{ class?: string }>) => (
	<AField.ErrorText {...props} class={cn('flex flex-col text-rose-500', props.class)} />
);

const ErrorContext = createContext<Accessor<Array<string>>>(() => []);
